import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { stringify } from "qs";
import { useAuth } from "src/hooks";
import { Query } from "src/types/query";
import type {
    SFAccount,
    SFAccountChild,
    SFAccountType,
    SFAccountTypeEnum,
    SFContact,
    SFUser,
} from "src/types/sales-force";
import axios from "src/utils/axios";

/** Account filters options */
interface SFAccountFilter {
    /** Name to filter LIKE% */
    name?: string;
    /** If should be linked or not with an orga */
    isLinkedWithOrga?: boolean;
    /** Account identifier */
    id?: string;
    /** Stripe Id */
    stripeId?: string;
    /** Account type */
    accountType?: SFAccountTypeEnum;
    /** Last modification date */
    modifiedSince?: moment.MomentInput;
}

/** Account parent filters options */
export interface SFAccountParentFilter extends SFAccountFilter {
    /** Organization ids */
    orgaIds?: number[];
}

/** Account child filters options */
export interface SFAccountChildFilter extends SFAccountFilter {
    /** Organization ids */
    orgaIds?: number[];
    /** Pos ids */
    posIds?: number[];
}

export function useQuerySalesForceAccounts(
    accountType: SFAccountType,
    filters: SFAccountParentFilter | SFAccountChildFilter
): UseQueryResult<SFAccount[] | SFAccountChild[]> {
    const { user } = useAuth();
    const queryString = stringify(filters);

    return useQuery({
        queryKey: [Query.SalesForceGetAccounts, user.id, accountType, queryString],

        queryFn: () =>
            axios
                .get<SFAccount[] | SFAccountChild[]>(
                    `/sales-force/accounts/${accountType}?${queryString}`
                )
                .then((res) => {
                    return res.data;
                }),
    });
}

/** Contact filters options */
export interface SFContactFilters {
    /** Account ids */
    accountIds: string[];
    /** If is flagged as referent */
    isContactReferent?: boolean;
}

export function useQuerySalesForceContacts(filters: SFContactFilters): UseQueryResult<SFContact[]> {
    const { user } = useAuth();
    const queryString = stringify(filters);

    return useQuery({
        queryKey: [Query.SalesForceGetAccounts, user.id, queryString],

        queryFn: () => {
            if (!filters.accountIds || !filters.accountIds.length) {
                return [];
            }

            return axios.get<SFContact[]>(`/sales-force/contacts?${queryString}`).then((res) => {
                return res.data;
            });
        },
    });
}

export function useQuerySFConnection(): UseQueryResult {
    const { user } = useAuth();

    return useQuery({
        queryKey: [Query.SalesForceGetConnection, user?.id],

        queryFn: () => {
            return axios.get("/sales-force/connection");
        },

        retry: false,
    });
}

export function useQuerySFUsers(): UseQueryResult<SFUser[]> {
    return useQuery({
        queryKey: [Query.SalesForceGetAccounts],

        queryFn: () => {
            return axios.get<SFUser[]>(`/sales-force/users`).then((res) => {
                return res.data;
            });
        },

        staleTime: 5 * 60 * 1000,
    });
}
